/**
 * The hard close of a round, as its engine published it INSIDE `race_ready`'s
 * payload (hard close rev 4, DESIGN §2, §6): `{window_s, lock_lead_s,
 * bet_close, hold_s}`. Present on the domain event only when the round is hard
 * (`bet_close: "hard"`); every other round carries none and runs today's
 * board.
 *
 * Nothing here decides money. Go cuts every bet at K = d + W on its own clock;
 * the board only locks, on the first of `race_starting`'s arrival and its own
 * countdown, and sends.
 */
export interface HardClose {
  /** W: the betting window the engine waits, in seconds. Null when the row's value is unusable. */
  windowS: number | null
  /** X: `race_starting`'s lead before the cut, in seconds; always in range (a bad value reads as 4). */
  lockLeadS: number
  /** R: the engine's hold after the cut, in seconds. Null: the legacy hold. */
  holdS: number | null
}

/** X when the payload's is missing or out of range: today's lead on every production game. */
export const DEFAULT_LOCK_LEAD_S = 4
/** The range the switch table enforces, and the board reads by (DESIGN §2.4). */
export const LOCK_LEAD_MIN_S = 2.5
export const LOCK_LEAD_MAX_S = 5

/**
 * W as Go bounds it (`MinBetWindowSeconds`..`MaxBetWindowSeconds`, inclusive).
 * Go seals a hard round whose W is outside it, so it takes no bet; a W outside
 * it (or at or under X) gives the board no countdown, and the board then locks
 * on `race_starting` alone (fails closed).
 */
export const MIN_WINDOW_S = 3
export const MAX_WINDOW_S = 60

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/** X as the board reads it: the payload's when it is 2.5 to 5 s, else 4. */
export function lockLeadOf(raw: unknown): number {
  const lead = finite(raw)
  if (lead === null || lead < LOCK_LEAD_MIN_S || lead > LOCK_LEAD_MAX_S) return DEFAULT_LOCK_LEAD_S
  return lead
}

/**
 * The round's hard close off `race_ready`'s wire payload (snake_case), or
 * undefined when the round is not hard. A malformed W keeps the round hard but
 * gives it no countdown: the board then locks on `race_starting` alone.
 */
export function hardCloseFromWire(payload: unknown): HardClose | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined
  const wire = payload as Record<string, unknown>
  if (wire.bet_close !== 'hard') return undefined
  const lockLeadS = lockLeadOf(wire.lock_lead_s)
  const window = finite(wire.window_s)
  const windowS =
    window !== null && window > lockLeadS && window >= MIN_WINDOW_S && window <= MAX_WINDOW_S
      ? window
      : null
  const hold = finite(wire.hold_s)
  return { windowS, lockLeadS, holdS: hold !== null && hold > 0 ? hold : null }
}
